import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ArrowRightLeft, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const NONE = 'none';

export const ReassignLesseeDialog = ({ open, onOpenChange, client, fromManager, el, onDone }) => {
    const [managers, setManagers] = useState([]);
    const [target, setTarget] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTarget('');
        api.get('/leasing/managers').then((r) => setManagers(r.data)).catch(() => setManagers([]));
    }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            await api.post('/leasing/managers/reassign', { client_id: client.id, to_manager_id: target === NONE ? null : target, from_manager_id: fromManager?.id || null });
            toast.success(el ? 'Η ανάθεση ενημερώθηκε' : 'Assignment updated');
            onOpenChange(false);
            onDone();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    const options = managers.filter((m) => m.id !== fromManager?.id);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white" data-testid="reassign-dialog">
                <DialogHeader>
                    <DialogTitle>{fromManager ? (el ? 'Μεταφορά μισθωτή' : 'Move lessee') : (el ? 'Ανάθεση μισθωτή' : 'Assign lessee')} - {client?.name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        {fromManager
                            ? (el ? `Από: ${fromManager.name}` : `From: ${fromManager.name}`)
                            : (el ? 'Ο μισθωτής θα ανατεθεί μόνο στον υπεύθυνο που θα επιλέξετε.' : 'The lessee will be assigned only to the manager you choose.')}
                    </p>
                    <div className="space-y-1">
                        <Label>{el ? 'Σε υπεύθυνο στόλου' : 'To fleet manager'}</Label>
                        <Select value={target} onValueChange={setTarget}>
                            <SelectTrigger data-testid="reassign-target-select"><SelectValue placeholder={el ? 'Επιλέξτε υπεύθυνο' : 'Select manager'} /></SelectTrigger>
                            <SelectContent className="bg-white">
                                {options.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.stats?.lessees ?? m.assigned_client_ids.length} {el ? 'μισθωτές' : 'lessees'})</SelectItem>)}
                                <SelectItem value={NONE}>{el ? 'Χωρίς υπεύθυνο' : 'No manager'}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={save} disabled={saving || !target} className="w-full bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="reassign-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRightLeft className="w-4 h-4 mr-2" />{el ? 'Επιβεβαίωση' : 'Confirm'}</>}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const UnassignedLesseesCard = ({ el, refreshKey = 0, onChanged }) => {
    const [items, setItems] = useState(null);
    const [client, setClient] = useState(null);

    const load = useCallback(() => {
        api.get('/leasing/unassigned').then((r) => setItems(r.data)).catch(() => setItems(null));
    }, []);

    useEffect(() => { load(); }, [load, refreshKey]);

    if (!items || !items.length) return null;

    return (
        <div className="fleet-card p-5 border-l-4 border-amber-500 bg-amber-50/40 space-y-3" data-testid="unassigned-lessees-card">
            <div className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-amber-600" />
                <h2 className="text-base md:text-lg font-semibold text-slate-800">{el ? 'Μισθωτές χωρίς υπεύθυνο στόλου' : 'Lessees without a fleet manager'} ({items.length})</h2>
            </div>
            <div className="flex flex-wrap gap-2">
                {items.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 bg-white border border-amber-200 rounded-sm pl-3 pr-1 py-1" data-testid={`unassigned-lessee-${c.id}`}>
                        <span className="text-sm text-slate-700">{c.name}</span>
                        <Button size="sm" variant="ghost" onClick={() => setClient(c)} className="h-7 text-[#1B7EB5]" data-testid={`unassigned-assign-${c.id}`}>{el ? 'Ανάθεση' : 'Assign'}</Button>
                    </div>
                ))}
            </div>
            <ReassignLesseeDialog open={Boolean(client)} onOpenChange={(o) => !o && setClient(null)} client={client} el={el} onDone={() => { load(); onChanged?.(); }} />
        </div>
    );
};
